import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTipoAreas, useTareasTipoArea } from '../hooks/useTipoAreas';
import { useTareas } from '../hooks/useTareas';
import { usePagination } from '../hooks/usePagination';
import { TipoArea, CreateTipoAreaForm, UpdateTipoAreaForm, CreateTareaTipoAreaDto } from '../types';

const TipoAreaTasksPanel: React.FC<{ tipoAreaId: number }> = ({ tipoAreaId }) => {
  const { tareas } = useTareas();
  const { 
    tareasTipoArea, 
    isLoading, 
    addTareaATipo, 
    removeTareaDeTipo, 
    // updateTareaTipoAreaConfig,
    isAddingTarea,
    isRemovingTarea,
    // isUpdatingConfig
  } = useTareasTipoArea(tipoAreaId);

  const [selectedTareaId, setSelectedTareaId] = useState<number | ''>('');
  const [tareaObligatoria, setTareaObligatoria] = useState(true);

  const tareasDisponibles = tareas.filter(t => 
    !tareasTipoArea.some(tt => tt.id === t.id)
  );

  const handleAddTarea = async () => {
    if (!selectedTareaId || typeof selectedTareaId !== 'number') return;
    const tarea = tareas.find(t => t.id === selectedTareaId);
    if (!tarea) return;

    const payload: CreateTareaTipoAreaDto = {
      nombre: tarea.nombre,
      descripcion: tarea.descripcion || '',
      obligatoria: tareaObligatoria,
    };
    await addTareaATipo({ tipoAreaId, payload });
    setSelectedTareaId('');
  };

  const handleRemoveTarea = async (tareaId: number) => {
    await removeTareaDeTipo({ tipoAreaId, tareaId });
  };

  return (
    <div className="mt-3">
      <div className="bg-gray-50 p-4 rounded-md mb-4">
        <h4 className="text-md font-medium text-gray-900 mb-3">Agregar Tarea</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tarea</label>
            <select
              value={selectedTareaId}
              onChange={(e) => setSelectedTareaId(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="">Seleccionar tarea</option>
              {tareasDisponibles.map(t => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id={`obligatoria-${tipoAreaId}`}
              type="checkbox"
              checked={tareaObligatoria}
              onChange={(e) => setTareaObligatoria(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor={`obligatoria-${tipoAreaId}`} className="text-sm text-gray-700">Obligatoria</label>
          </div>
          <div>
            <button
              onClick={handleAddTarea}
              disabled={!selectedTareaId || isAddingTarea}
              className={`px-4 py-2 rounded-md ${
                !selectedTareaId || isAddingTarea 
                  ? 'bg-indigo-300 text-white cursor-not-allowed' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {isAddingTarea ? 'Agregando...' : 'Agregar Tarea'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-md">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h4 className="text-md font-medium text-gray-900">Tareas Asignadas</h4>
        </div>
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">Cargando tareas...</div>
        ) : tareasTipoArea.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No hay tareas asignadas a este tipo de área</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-2">Nombre</th>
                <th className="px-4 py-2">Descripción</th>
                <th className="px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {tareasTipoArea.map((tt) => (
                <tr key={tt.id} className="border-t">
                  <td className="px-4 py-2">{tt.nombre}</td>
                  <td className="px-4 py-2">{tt.descripcion || '-'}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleRemoveTarea(tt.id)}
                      disabled={isRemovingTarea}
                      className="text-red-600 hover:text-red-900 hover:bg-red-50 text-sm font-medium px-3 py-1 rounded transition-colors disabled:opacity-50"
                    >
                      {isRemovingTarea ? 'Removiendo...' : 'Remover'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

interface FormErrors {
  nombre?: string;
  descripcion?: string;
  frecuenciaLimpiezaHoras?: string;
  porcentajeAmarillo?: string;
  porcentajeRojo?: string;
}

const TipoAreas: React.FC = () => {
  const { 
    tipoAreas, 
    // tipoAreasActivos, // No utilizado actualmente
    // isLoading,  // No utilizado actualmente
    // isLoadingActivos, // No utilizado actualmente
    // error, // No utilizado actualmente
    createTipoArea, 
    updateTipoArea, 
    deleteTipoArea, 
    isCreating, 
    isUpdating, 
    isDeleting,
  } = useTipoAreas();
  
  const [showForm, setShowForm] = useState(false);
  const [editingTipoArea, setEditingTipoArea] = useState<TipoArea | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState<CreateTipoAreaForm>({
    nombre: '',
    descripcion: '',
    frecuenciaLimpiezaHoras: 24,
    porcentajeAmarillo: 75,
    porcentajeRojo: 100,
  });

  const [expandedTipoAreaId, setExpandedTipoAreaId] = useState<number | null>(null);
  const location = useLocation();

  // Limpiar mensajes de éxito después de 3 segundos
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      errors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    if (formData.descripcion && formData.descripcion.length > 500) {
      errors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }

    if (formData.frecuenciaLimpiezaHoras < 1) {
      errors.frecuenciaLimpiezaHoras = 'La frecuencia debe ser mayor a 0 horas';
    }

    if (formData.porcentajeAmarillo < 0 || formData.porcentajeAmarillo > 100) {
      errors.porcentajeAmarillo = 'El porcentaje amarillo debe estar entre 0 y 100';
    }

    if (formData.porcentajeRojo < 0 || formData.porcentajeRojo > 100) {
      errors.porcentajeRojo = 'El porcentaje rojo debe estar entre 0 y 100';
    }

    if (formData.porcentajeAmarillo >= formData.porcentajeRojo) {
      errors.porcentajeAmarillo = 'El porcentaje amarillo debe ser menor al rojo';
      errors.porcentajeRojo = 'El porcentaje rojo debe ser mayor al amarillo';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar tipos de áreas según búsqueda y filtro de estado (global, luego paginar)
  const filteredTipoAreas = useMemo(() => {
    const sourceTipoAreas = tipoAreas;
    const term = searchTerm.trim().toLowerCase();
    return sourceTipoAreas.filter(tipoArea => {
      const matchesSearch = term === '' ||
        tipoArea.nombre.toLowerCase().includes(term) ||
        (tipoArea.descripcion?.toLowerCase().includes(term) ?? false);
      const matchesEstado = 
        filterEstado === 'todos' ||
        (filterEstado === 'activos' && tipoArea.activo) ||
        (filterEstado === 'inactivos' && !tipoArea.activo);
      return matchesSearch && matchesEstado;
    });
  }, [tipoAreas, searchTerm, filterEstado]);

  const { page, setPage, pageSize, totalItems, totalPages, pageStart, pageEnd, pageItems, goPrevPage, goNextPage } = usePagination(filteredTipoAreas);

  // Reset de página al cambiar búsqueda/filtro
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterEstado, setPage]);

  // Asegurar que la página actual siempre sea válida cuando cambie el total
  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(filteredTipoAreas.length / pageSize));
    if (page > newTotalPages - 1) {
      setPage(newTotalPages - 1);
    }
  }, [filteredTipoAreas.length, page, pageSize, setPage]);

  // Al entrar a la ruta, hacer scroll arriba y resetear a primera página
  useEffect(() => {
    // Si la ruta contiene tipo-areas, resetea y sube
    if (location.pathname.includes('/tipo-areas')) {
      setPage(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, setPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (editingTipoArea) {
        const updateData: UpdateTipoAreaForm = {
          ...formData,
          activo: editingTipoArea.activo
        };
        await updateTipoArea({ id: editingTipoArea.id, tipoArea: updateData });
        setSuccessMessage('Tipo de área actualizado exitosamente');
      } else {
        await createTipoArea(formData);
        setSuccessMessage('Tipo de área creado exitosamente');
      }
      setShowSuccessMessage(true);
      handleCancel();
    } catch (error) {
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTipoArea(null);
    setFormErrors({});
    setFormData({
      nombre: '',
      descripcion: '',
      frecuenciaLimpiezaHoras: 24,
      porcentajeAmarillo: 75,
      porcentajeRojo: 100,
    });
  };

  const handleEdit = (tipoArea: TipoArea) => {
    setEditingTipoArea(tipoArea);
    setFormData({
      nombre: tipoArea.nombre,
      descripcion: tipoArea.descripcion || '',
      frecuenciaLimpiezaHoras: tipoArea.frecuenciaLimpiezaHoras,
      porcentajeAmarillo: tipoArea.porcentajeAmarillo,
      porcentajeRojo: tipoArea.porcentajeRojo,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tipo de área? Esta acción no se puede deshacer.')) {
      try {
        await deleteTipoArea(id);
        setSuccessMessage('Tipo de área eliminado exitosamente');
        setShowSuccessMessage(true);
      } catch (error) {
      }
    }
  };

  const handleToggleActivo = async (tipoArea: TipoArea) => {
    try {
      const updateData: UpdateTipoAreaForm = {
        nombre: tipoArea.nombre,
        descripcion: tipoArea.descripcion || '',
        frecuenciaLimpiezaHoras: tipoArea.frecuenciaLimpiezaHoras,
        porcentajeAmarillo: tipoArea.porcentajeAmarillo,
        porcentajeRojo: tipoArea.porcentajeRojo,
        activo: !tipoArea.activo
      };
      await updateTipoArea({ id: tipoArea.id, tipoArea: updateData });
      setSuccessMessage(`Tipo de área ${!tipoArea.activo ? 'activado' : 'desactivado'} exitosamente`);
      setShowSuccessMessage(true);
    } catch (error) {
    }
  };

  const handleInputChange = (field: keyof CreateTipoAreaForm, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Gestión de Tipos de Áreas</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Administra los tipos de áreas de la clínica</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Nuevo Tipo de Área
          </button>
        </div>

        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Estado
              </label>
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value as 'todos' | 'activos' | 'inactivos')}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todos los tipos</option>
                <option value="activos">Solo activos</option>
                <option value="inactivos">Solo inactivos</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs sm:text-sm text-gray-600">
                {totalItems} tipo{totalItems !== 1 ? 's' : ''} encontrado{totalItems !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="bg-white shadow rounded-lg p-3 sm:p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingTipoArea ? 'Editar Tipo de Área' : 'Nuevo Tipo de Área'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.nombre ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    rows={3}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.descripcion ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Frecuencia de Limpieza (horas) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.frecuenciaLimpiezaHoras}
                    onChange={(e) => handleInputChange('frecuenciaLimpiezaHoras', parseInt(e.target.value))}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.frecuenciaLimpiezaHoras ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.frecuenciaLimpiezaHoras && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.frecuenciaLimpiezaHoras}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Porcentaje Amarillo (%) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    value={formData.porcentajeAmarillo}
                    onChange={(e) => handleInputChange('porcentajeAmarillo', parseInt(e.target.value))}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.porcentajeAmarillo ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.porcentajeAmarillo && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.porcentajeAmarillo}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Porcentaje Rojo (%) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    value={formData.porcentajeRojo}
                    onChange={(e) => handleInputChange('porcentajeRojo', parseInt(e.target.value))}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.porcentajeRojo ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.porcentajeRojo && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.porcentajeRojo}</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="order-2 sm:order-1 bg-gray-300 text-gray-700 px-4 py-3 w-full sm:w-auto rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className="order-1 sm:order-2 bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  {isCreating || isUpdating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </div>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {totalItems === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm || filterEstado !== 'todos' 
                  ? 'No se encontraron tipos de áreas que coincidan con los filtros.' 
                  : 'No hay tipos de áreas disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pageItems.map((tipoArea) => (
                <li key={tipoArea.id}>
                  <div className="px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                          tipoArea.activo ? 'bg-green-500' : 'bg-gray-400'
                        }`}>
                          <span className="text-white text-sm font-medium">
                            {tipoArea.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <div className="text-sm sm:text-base font-medium text-gray-900">
                          {tipoArea.nombre}
                        </div>
                        {tipoArea.descripcion && (
                          <div className="text-xs sm:text-sm text-gray-500">
                            {tipoArea.descripcion}
                          </div>
                        )}
                          <div className="flex flex-wrap gap-2 sm:gap-3 mt-1">
                            <div className={`text-xs sm:text-sm px-2 py-1 rounded-full ${
                              tipoArea.activo ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                            }`}>
                              {tipoArea.activo ? 'Activo' : 'Inactivo'}
                            </div>
                            <div className="text-xs sm:text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              Frecuencia: {tipoArea.frecuenciaLimpiezaHoras}h
                            </div>
                            <div className="text-xs sm:text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                              Alertas: {tipoArea.porcentajeAmarillo}% / {tipoArea.porcentajeRojo}%
                            </div>
                          </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => setExpandedTipoAreaId(prev => prev === tipoArea.id ? null : tipoArea.id)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md"
                        >
                          {expandedTipoAreaId === tipoArea.id ? 'Ocultar Tareas' : 'Gestionar Tareas'}
                        </button>
                      <button
                        onClick={() => handleToggleActivo(tipoArea)}
                        disabled={isUpdating}
                          className={`text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md ${
                          tipoArea.activo 
                              ? 'text-yellow-600 hover:text-yellow-900' 
                              : 'text-green-600 hover:text-green-900'
                        } disabled:opacity-50`}
                      >
                        {tipoArea.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        onClick={() => handleEdit(tipoArea)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(tipoArea.id)}
                        disabled={isDeleting}
                          className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50 py-3 px-3 w-full sm:w-auto text-center rounded-md"
                      >
                        Eliminar
                      </button>
                    </div>
                    </div>

                    {expandedTipoAreaId === tipoArea.id && (
                      <TipoAreaTasksPanel tipoAreaId={tipoArea.id} />)
                    }
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Paginación */}
        {totalItems > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600">
              Mostrando {pageStart + 1}–{pageEnd} de {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrevPage}
                disabled={page === 0}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página anterior"
              >
                ← Anterior
              </button>
              <span className="text-xs sm:text-sm text-gray-600">
                Página {page + 1} de {totalPages}
              </span>
              <button
                onClick={goNextPage}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página siguiente"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TipoAreas; 